import { Collapsable } from 'components/common/Collapsable'
import { Popover } from 'components/common/Popover'
import { Tag } from 'components/common/Tag'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { Edition } from '../../utils/data'

type Props = {
  isVerified?: boolean
  isNewCondition?: boolean
  currentEdition?: Edition | null
  showAdditionalVerification?: boolean
  keepTags?: boolean
  tagSize?: 'S' | 'M'
  tagsContentClassName?: string
}

export const ArtworkTags = ({
  isVerified,
  isNewCondition,
  currentEdition,
  showAdditionalVerification = true,
  keepTags = false,
  tagSize = 'M',
  tagsContentClassName,
}: Props) => {
  const { t } = useTranslation('common')

  const requiresAdditionalVerification = Boolean(
    currentEdition?.additionalCertification.artworkImages ||
      currentEdition?.additionalCertification.coaImages ||
      currentEdition?.additionalCertification.editionNumber ||
      currentEdition?.additionalCertification.provenanceImages
  )

  const collapsableContent = (
    <div className='flex flex-col items-start space-y-4'>
      {requiresAdditionalVerification && (
        <div className='w-full cursor-pointer  lg:w-fit lg:p-0'>
          <Collapsable
            iconClassName={'!text-cool-500'}
            title={
              <Tag
                icon='filled-inspect'
                iconFilled
                iconSize={24}
                iconClassName='!text-secondary-default'
                className='-ml-2 bg-transparent'
              >
                <span className='text-base font-medium text-cool-800'>
                  {t('additional_verification')}
                </span>
              </Tag>
            }
          >
            <p className='mr-6 mt-2 text-sm'>
              {t('provide_additional_certification')}{' '}
              <Link
                href='/how-it-works/guarantee'
                className='text-secondary-default'
                target='_blank'
                rel='noreferrer'
              >
                {t('learn_more')}

                <span className='sr-only'>
                  {t('common:about_additional_verification')}
                </span>
              </Link>
            </p>
          </Collapsable>
        </div>
      )}

      {isVerified && (
        <div className='w-full cursor-pointer lg:w-fit lg:p-0'>
          <Collapsable
            iconClassName={'!text-cool-500'}
            title={
              <Tag
                icon='filled-shield'
                iconFilled
                iconSize={24}
                iconClassName='!text-cool-800'
                className='-ml-2 bg-transparent'
              >
                <span className='text-base font-medium text-cool-800'>
                  {t('fairart_guarantee')}
                </span>
              </Tag>
            }
          >
            <p className='mr-6 mt-2 text-sm'>
              {t('we_dont_mess')}{' '}
              <Link
                href='/how-it-works/guarantee'
                className='text-secondary-default'
                target='_blank'
                rel='noreferrer'
              >
                {t('fairart_guarantee')}
              </Link>
              . {t('item_or_money')}{' '}
            </p>
          </Collapsable>
        </div>
      )}

      {isNewCondition && (
        <div className='w-full cursor-pointer lg:w-fit lg:p-0'>
          <Collapsable
            iconClassName={'!text-cool-500'}
            title={
              <Tag
                icon='filled-new'
                iconFilled
                iconSize={24}
                iconClassName='!text-cool-800'
                className='-ml-2 bg-transparent'
              >
                <span className='text-base font-medium text-cool-800'>
                  {t('new_condition')}
                </span>
              </Tag>
            }
          >
            <p className='mr-6 mt-2 text-sm'>
              {t('no_flaws')}{' '}
              <Link
                href='/how-it-works/guarantee?section=newCondition'
                className='text-secondary-default'
                target='_blank'
                rel='noreferrer'
              >
                {t('learn_more')}

                <span className='sr-only'>
                  {t('common:about_new_condition')}
                </span>
              </Link>
            </p>
          </Collapsable>
        </div>
      )}
    </div>
  )

  const tagsContent = (
    <div
      className={`mb-8 flex flex-wrap items-start space-x-4 lg:flex-row lg:items-center ${tagsContentClassName}`}
    >
      {isVerified && (
        <div className='lg:w-fit lg:p-0'>
          <div className='flex w-full items-center justify-between lg:block'>
            <Popover
              side='top'
              content={
                <p className='text-sm'>
                  {t('we_dont_mess')}{' '}
                  <Link
                    href='/how-it-works/guarantee'
                    className='text-secondary-default'
                    target='_blank'
                    rel='noreferrer'
                  >
                    {t('fairart_guarantee')}
                  </Link>
                  . {t('item_or_money')}{' '}
                </p>
              }
            >
              <Tag
                icon='guarantee'
                iconSize={16}
                iconClassName='text-cool-100'
                size={tagSize}
              >
                {t('fairart_guarantee')}
              </Tag>
            </Popover>
          </div>
        </div>
      )}

      {isNewCondition && (
        <div className='lg:w-fit lg:p-0'>
          <div className='flex w-full items-center justify-between lg:block'>
            <Popover
              side='top'
              content={
                <p className='text-sm'>
                  {t('no_flaws')}{' '}
                  <Link
                    href='/how-it-works/guarantee#newCondition'
                    className='text-secondary-default'
                    target='_blank'
                    rel='noreferrer'
                  >
                    {t('learn_more')}

                    <span className='sr-only'>
                      {t('common:about_new_condition')}
                    </span>
                  </Link>
                </p>
              }
            >
              <Tag
                icon='new-2'
                iconSize={16}
                iconClassName='text-cool-100'
                size={tagSize}
              >
                {t('new_condition')}
              </Tag>
            </Popover>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <>
      {!keepTags && <div className='lg:hidden'>{collapsableContent}</div>}

      {requiresAdditionalVerification && showAdditionalVerification && (
        <div className='hidden lg:flex'>
          <div className='mb-6 w-full cursor-pointer lg:w-fit lg:p-0'>
            <Collapsable
              iconClassName={'!text-cool-500'}
              title={
                <Tag
                  icon='filled-inspect'
                  iconFilled
                  iconSize={24}
                  iconClassName='!text-secondary-default'
                  className='-ml-2 bg-transparent'
                >
                  <span className='text-base font-medium text-cool-800'>
                    {t('additional_verification')}
                  </span>
                </Tag>
              }
            >
              <p className='mr-6 mt-2 text-sm'>
                {t('provide_additional_certification')}{' '}
                <Link
                  href='/how-it-works/guarantee'
                  className='text-secondary-default'
                  target='_blank'
                  rel='noreferrer'
                >
                  {t('learn_more')}

                  <span className='sr-only'>
                    {t('single_artwork:about_additional_verification')}
                  </span>
                </Link>
              </p>
            </Collapsable>
          </div>
        </div>
      )}

      {keepTags ? (
        tagsContent
      ) : (
        <div className='hidden lg:block'>{tagsContent}</div>
      )}
    </>
  )
}
